// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ApprovalRequestForm.test.tsx
import { type ParticipationTask } from "@mg/schemas/src/christo/participationTask";
import { Image as ImageIcon, X } from "@phosphor-icons/react";
import { Button, IconButton } from "@radix-ui/themes";
import { useState } from "react";

export type ApprovalRequestAction =
  | "approved"
  | "changes_requested"
  | "not_needed";

/** Visual metadata for a pending weigh-in card (from reviews panel / task). */
export type ApprovalRequestAssetVisual = {
  name: string;
  versionNumber?: number;
  thumbnailUrl?: string | null;
};

/** Parse denorm labels like "Packaging v2" into name + version. */
export function parseAssetLabel(assetLabel: string | null | undefined): {
  name: string | null;
  versionNumber?: number;
} {
  const trimmed = assetLabel?.trim();
  if (!trimmed) return { name: null };
  const match = trimmed.match(/^(.*)\s+v(\d+)\s*$/i);
  if (!match) return { name: trimmed };
  return {
    name: match[1]?.trim() || trimmed,
    versionNumber: Number(match[2]),
  };
}

function resolveCardVisual(
  task: ParticipationTask,
  visual?: ApprovalRequestAssetVisual | null,
): ApprovalRequestAssetVisual {
  if (visual?.name) {
    return {
      name: visual.name,
      versionNumber: visual.versionNumber,
      thumbnailUrl: visual.thumbnailUrl ?? null,
    };
  }
  const parsed = parseAssetLabel(task.assetLabel);
  return {
    name: parsed.name ?? "Review asset",
    versionNumber: parsed.versionNumber,
    thumbnailUrl: null,
  };
}

/**
 * Stacked in-feed approval cards: one per open participation task.
 * Presentational — parent owns mutations, busy state, and asset visuals.
 */
export type ApprovalCardHighlightTarget = {
  fileId: string;
  versionId: string;
  ticketId: string | null;
};

type ApprovalRespondInput = {
  task: ParticipationTask;
  action: ApprovalRequestAction;
  noteBody?: string;
};

function ApprovalRequestCard({
  task,
  busy,
  visual,
  compact,
  onRespond,
  onCardHighlight,
}: {
  task: ParticipationTask;
  busy: boolean;
  visual: ApprovalRequestAssetVisual;
  compact: boolean;
  onRespond: (input: ApprovalRespondInput) => void;
  onCardHighlight?: (target: ApprovalCardHighlightTarget | null) => void;
}) {
  const [denyStep, setDenyStep] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  const kindLabel = task.kind === "invited" ? "Invited" : "Suggested";
  const title = visual.name;
  const versionLabel =
    visual.versionNumber != null ? `v${visual.versionNumber}` : null;
  const highlightTarget: ApprovalCardHighlightTarget = {
    fileId: task.fileId,
    versionId: task.versionId,
    ticketId: task.ticketId,
  };

  const cancelDenyStep = () => {
    setDenyStep(false);
    setNoteDraft("");
  };

  const confirmDeny = () => {
    const trimmed = noteDraft.trim();
    onRespond({
      task,
      action: "changes_requested",
      ...(trimmed ? { noteBody: trimmed } : {}),
    });
    setDenyStep(false);
    setNoteDraft("");
  };

  return (
    <section
      className={
        compact
          ? "relative overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white p-3 shadow-sm outline-none focus-within:border-puntt-accent-7 focus-within:ring-2 focus-within:ring-puntt-accent-6/30"
          : "relative overflow-hidden rounded-2xl border border-puntt-neutral-gray-5 bg-base-white p-4 shadow-sm outline-none focus-within:border-puntt-accent-7 focus-within:ring-2 focus-within:ring-puntt-accent-6/30"
      }
      data-testid={`approval-request-card-${task.versionId}`}
      aria-label={`Weigh in on ${title}`}
      onMouseEnter={() => onCardHighlight?.(highlightTarget)}
      onMouseLeave={() => onCardHighlight?.(null)}
      onFocusCapture={() => onCardHighlight?.(highlightTarget)}
      onBlurCapture={(e) => {
        const next = e.relatedTarget as Node | null;
        if (!next || !e.currentTarget.contains(next)) {
          onCardHighlight?.(null);
        }
      }}
    >
      <IconButton
        type="button"
        size="1"
        variant="ghost"
        color="gray"
        className="absolute right-2 top-2 z-10"
        data-testid={`approval-request-not-needed-${task.versionId}`}
        aria-label="Not needed from me"
        disabled={busy}
        onClick={() => onRespond({ task, action: "not_needed" })}
      >
        <X size={14} aria-hidden="true" />
      </IconButton>

      <div className="mb-1 flex flex-wrap items-center gap-2 pr-7">
        <p className="text-[11px] font-medium uppercase tracking-wide text-puntt-neutral-gray-10">
          Needs your decision
        </p>
        <span
          className="rounded-full bg-puntt-neutral-gray-3 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-puntt-neutral-gray-11"
          data-testid={`approval-request-kind-${task.versionId}`}
        >
          {kindLabel}
        </span>
        {task.suggestedRole ? (
          <span
            className="text-[11px] text-puntt-neutral-gray-10"
            data-testid={`approval-request-role-${task.versionId}`}
          >
            {task.suggestedRole}
          </span>
        ) : null}
      </div>

      <div
        className={
          compact
            ? "mt-2 flex items-stretch gap-2.5"
            : "mt-3 flex items-stretch gap-3"
        }
        data-testid={`approval-request-asset-${task.versionId}`}
      >
        <span
          aria-hidden="true"
          data-testid={`approval-request-thumbnail-${task.versionId}`}
          className={
            compact
              ? "flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11"
              : "flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11"
          }
        >
          {visual.thumbnailUrl ? (
            // eslint-disable-next-line jsx-a11y/alt-text -- decorative; card has aria-label
            <img
              src={visual.thumbnailUrl}
              alt=""
              aria-hidden="true"
              className="size-full object-cover"
            />
          ) : (
            <ImageIcon size={22} weight="fill" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <h3
            className={
              compact
                ? "truncate text-[14px] font-semibold text-puntt-neutral-gray-12"
                : "truncate text-[15px] font-semibold text-puntt-neutral-gray-12"
            }
            data-testid={`approval-request-title-${task.versionId}`}
          >
            {title}
          </h3>
          {versionLabel ? (
            <p
              className="mt-0.5 text-[12px] text-puntt-neutral-gray-10"
              data-testid={`approval-request-version-${task.versionId}`}
            >
              {versionLabel}
            </p>
          ) : null}
          {task.reasoning?.trim() ? (
            <p
              className="mt-2 text-[12px] leading-relaxed text-puntt-neutral-gray-11"
              data-testid={`approval-request-reasoning-${task.versionId}`}
            >
              {task.reasoning.trim()}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        {!denyStep ? (
          <div
            className="flex items-center gap-2"
            role="group"
            aria-label={`Choose a response for ${title}`}
          >
            <Button
              type="button"
              size="1"
              data-testid={`approval-request-approve-${task.versionId}`}
              aria-label="Approve"
              disabled={busy}
              onClick={() => onRespond({ task, action: "approved" })}
            >
              {busy ? "Submitting…" : "Approve"}
            </Button>
            <Button
              type="button"
              size="1"
              variant="outline"
              color="gray"
              data-testid={`approval-request-deny-${task.versionId}`}
              aria-label="Deny"
              disabled={busy}
              onClick={() => setDenyStep(true)}
            >
              Deny
            </Button>
          </div>
        ) : (
          <div
            className="approval-deny-slide-in flex flex-col gap-2"
            role="group"
            aria-label={`Add an optional note before denying ${title}`}
            data-testid={`approval-request-deny-step-${task.versionId}`}
          >
            <textarea
              className="w-full resize-none rounded-md border border-puntt-neutral-gray-6 bg-base-white px-2.5 py-2 text-[13px] text-puntt-neutral-gray-12 placeholder:text-puntt-neutral-gray-9 focus:border-puntt-accent-7 focus:outline-none focus:ring-2 focus:ring-puntt-accent-6/30 disabled:cursor-not-allowed disabled:opacity-60"
              rows={2}
              placeholder="Optional note (why you're requesting changes)"
              value={noteDraft}
              disabled={busy}
              data-testid={`approval-request-deny-note-${task.versionId}`}
              aria-label="Optional note for requested changes"
              onChange={(event) => setNoteDraft(event.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="1"
                data-testid={`approval-request-deny-confirm-${task.versionId}`}
                aria-label="Submit deny"
                disabled={busy}
                onClick={confirmDeny}
              >
                {busy ? "Submitting…" : "Deny"}
              </Button>
              <Button
                type="button"
                size="1"
                variant="outline"
                color="gray"
                data-testid={`approval-request-deny-cancel-${task.versionId}`}
                aria-label="Cancel deny"
                disabled={busy}
                onClick={cancelDenyStep}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export function ApprovalRequestForm({
  tasks,
  busyVersionId = null,
  assetsByVersionId = {},
  onRespond,
  onCardHighlight,
  variant = "default",
}: {
  tasks: ParticipationTask[];
  busyVersionId?: string | null;
  /** Thumbnail / display name keyed by versionId (from project reviews). */
  assetsByVersionId?: Record<string, ApprovalRequestAssetVisual>;
  onRespond: (input: ApprovalRespondInput) => void;
  /** Hover/focus on a card — parent highlights the matching Reviews sidebar row. */
  onCardHighlight?: (target: ApprovalCardHighlightTarget | null) => void;
  /** Denser layout for home popover and other compact surfaces. */
  variant?: "default" | "compact";
}) {
  const compact = variant === "compact";

  if (tasks.length === 0) return null;

  return (
    <div
      className={compact ? "flex flex-col gap-2" : "flex flex-col gap-3"}
      data-testid="approval-request-form"
      aria-label="Pending approval requests"
    >
      {tasks.map((task) => {
        const busy = busyVersionId === task.versionId;
        const visual = resolveCardVisual(
          task,
          assetsByVersionId[task.versionId],
        );

        return (
          <ApprovalRequestCard
            key={task.versionId}
            task={task}
            busy={busy}
            visual={visual}
            compact={compact}
            onRespond={onRespond}
            onCardHighlight={onCardHighlight}
          />
        );
      })}
    </div>
  );
}
